import React from 'react';
import Modal from '../../../base/modal';
import { IGetBeneficiary } from '../../../../models/beneficiary.model';
import Verified from '../../../../assets/svgs/verified.svg';
import PaymentBadge from '../../../base/payment-badge';

interface BeneficiaryDetailsProps {
  isOpen: boolean;
  toggleModal: () => void;
  selectedBeneficiary: IGetBeneficiary | null;
}

const BeneficiaryDetails: React.FC<BeneficiaryDetailsProps> = ({
  isOpen,
  toggleModal,
  selectedBeneficiary,
}) => {
  return (
    <Modal isOpen={isOpen} toggleModal={toggleModal} width="w-[946px] h-fit">
      <h2 className="text-xl font-bold">Beneficiary Details</h2>

      <div className="flex flex-col gap-6 rounded-xl border border-[#F1F2F4] mt-12 mb-40 px-6 pt-6 mx-6">
        <div className="flex justify-between items-center border-b pb-6">
          <div>
            <p className="font-bold text-xl">
              {selectedBeneficiary?.accountHolderName}
            </p>
            <p className="text-sm text-gray-500">
              {selectedBeneficiary?.beneficiaryEmail}
            </p>
          </div>

          <div>
            <div
              className={`py-2 px-3 rounded-[10px] flex gap-[10px] ${
                selectedBeneficiary?.isVerified
                  ? 'bg-[#55C790]'
                  : 'bg-[#F1F2F4]'
              }`}
            >
              {selectedBeneficiary?.isVerified && <img src={Verified} alt="" />}
              <p
                className={`${
                  selectedBeneficiary?.isVerified
                    ? 'text-white'
                    : ' text-[#687588]'
                }`}
              >
                {selectedBeneficiary?.isVerified ? 'Verified' : 'Unverified'}
              </p>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <p>Bank Information</p>
        </div>

        <div className="border-b pb-6">
          <div className="grid grid-cols-5 gap-5">
            <div className="flex flex-col gap-2">
              <p className="text-sm text-[#687588] font-normal">Bank Name</p>
              <p className="font-semibold text-sm text-[#111827]">
                {selectedBeneficiary?.bankName}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm text-[#687588] font-normal">Account Name</p>
              <p className="font-semibold text-sm text-[#111827]">
                {selectedBeneficiary?.accountHolderName}
              </p>
            </div>

            {selectedBeneficiary?.paymentType === 1 && (
              <>
                <div className="flex flex-col gap-2 col-span-2">
                  <p className="text-sm text-[#687588] font-normal">
                    IBAN Account Number
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.swiftPayment?.ibanAccount.ibanAccount}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Country Code
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.swiftPayment?.ibanAccount.countryCode}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    BIC/Swift Code
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.swiftPayment?.ibanAccount.bic}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Payment Type
                  </p>
                  <PaymentBadge paymentType={1} />
                </div>
              </>
            )}

            {selectedBeneficiary?.paymentType === 2 && (
              <>
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    BIC/Swift Code
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.ibanPayment?.bic}
                  </p>
                </div>

                <div className="flex flex-col gap-2 col-span-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Account Number
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.ibanPayment?.ibanAccount}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Country Code
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.ibanPayment?.countryCode}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Payment Type
                  </p>
                  <PaymentBadge paymentType={2} />
                </div>
              </>
            )}

            {selectedBeneficiary?.paymentType === 3 && (
              <>
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Account Number
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.localAccountPayment?.accountNumber}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Routing Number
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.localAccountPayment?.routingNumber}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Account Type
                  </p>
                  <p className="font-semibold text-sm text-[#111827] capitalize">
                    {
                      selectedBeneficiary?.localAccountPayment
                        ?.checkingOrSavings
                    }
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Payment Type
                  </p>
                  <PaymentBadge paymentType={3} />
                </div>
              </>
            )}

            {selectedBeneficiary?.paymentType === 4 && (
              <>
                <div className="flex flex-col gap-2 col-span-2">
                  <p className="text-sm text-[#687588] font-normal">
                    BIC/Swift Code
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.swiftUnknown?.bic}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Account Number
                  </p>
                  <p className="font-semibold text-sm text-[#111827]">
                    {selectedBeneficiary?.swiftUnknown?.accountNumber}
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm text-[#687588] font-normal">
                    Payment Type
                  </p>
                  <PaymentBadge paymentType={4} />
                </div>
              </>
            )}

            <div className="flex flex-col gap-2">
              <p className="text-sm text-[#687588] font-normal">Currency</p>
              <p className="font-semibold text-sm text-[#111827]">
                {selectedBeneficiary?.currencyCode}
              </p>
            </div>

            <div className="flex flex-col gap-2 col-span-2">
              <p className="text-sm text-[#687588] font-normal">
                Email Address
              </p>
              <p className="font-semibold text-sm text-[#111827]">
                {selectedBeneficiary?.beneficiaryEmail}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm text-[#687588] font-normal">
                Verification Status
              </p>
              <p
                className={`font-semibold text-sm ${
                  selectedBeneficiary?.isVerified
                    ? 'text-green-600'
                    : 'text-yellow-600'
                }`}
              >
                {selectedBeneficiary?.isVerified
                  ? 'Verified'
                  : 'Pending Verification'}
              </p>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <p>Location Information</p>
        </div>

        <div className="pb-6">
          <div className="grid grid-cols-5 gap-5">
            <div className="flex flex-col gap-2">
              <p className="text-sm text-[#687588] font-normal">Region</p>
              <p className="font-semibold text-sm text-[#111827]">
                {selectedBeneficiary?.beneficiaryAddress?.countryCode}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm text-[#687588] font-normal">Postal Code</p>
              <p className="font-semibold text-sm text-[#111827]">
                {selectedBeneficiary?.beneficiaryAddress?.postalCode}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm text-[#687588] font-normal">City</p>
              <p className="font-semibold text-sm text-[#111827]">
                {selectedBeneficiary?.beneficiaryAddress?.city}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BeneficiaryDetails;
